import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Subject, throwError } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { ApiKeysService, NotificationService } from '../../../core/services';
import { ModalService, bindModalToQueryParam } from '../../../shared/modal';
import { CreateKeyModal } from '../create-key-modal/create-key-modal';
import { apiErrorMessage } from '../../../core/utils/api-error-message';
import { ApiKey } from '../../../core/models';

@Component({
  selector: 'app-api-keys',
  standalone: false,
  templateUrl: './api-keys.html',
  styleUrl: './api-keys.scss',
})
export class ApiKeys implements OnInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();

  keys: ApiKey[] = [];
  isLoading = false;

  private readonly revealedValues = new Map<string, string>();
  private readonly revealingIds = new Set<string>();
  private readonly copiedIds = new Set<string>();

  get activeKeys(): ApiKey[] {
    return this.keys.filter(k => k.isActive);
  }

  get revokedKeys(): ApiKey[] {
    return this.keys.filter(k => !k.isActive);
  }

  // Field, not the constructor's modalService: ?create=1 on load opens the
  // dialog while fields are still being set up.
  private readonly modals = inject(ModalService);

  /** Create-key dialog, tied to ?create= so /api-keys/create (redirected
   *  here) and Back/refresh behave like the page it replaced. Any close
   *  reloads the list: Done, or ✕ after the key was already made. */
  readonly createModal = bindModalToQueryParam({
    param: 'create',
    open: () => {
      const ref = this.modals.small(CreateKeyModal);
      ref.closed$.subscribe(() => this.loadKeys());
      return ref;
    },
  });

  constructor(
    private apiKeysService: ApiKeysService,
    private notificationService: NotificationService,
    private modalService: ModalService,
  ) {}

  ngOnInit(): void {
    this.loadKeys();
  }

  loadKeys(): void {
    this.isLoading = true;
    this.apiKeysService.getApiKeys()
      .pipe(
        takeUntil(this._destroy$),
        catchError(error => {
          this.notificationService.error('Failed to load API keys. Please try again later.');
          return throwError(() => error);
        }),
        tap(keys => { this.keys = keys; }),
        finalize(() => { this.isLoading = false; }),
      ).subscribe();
  }

  revoke(key: ApiKey): void {
    // The request runs inside the confirm: pending state while it's in
    // flight, inline error (and a retry) if it fails, closes on success.
    this.modalService.confirm({
      title: 'Revoke API key?',
      message: `This will permanently revoke the '${key.name}' key. Any services using it will stop working immediately.`,
      confirmLabel: 'Revoke key',
      pendingLabel: 'Revoking…',
      danger: true,
      onConfirm: () => this.apiKeysService.revokeApiKey(key.id).pipe(
        takeUntil(this._destroy$),
        tap(revoked => {
          // Revoking never deletes replace the row in place so it stays visible as revoked.
          this.keys = this.keys.map(k => (k.id === revoked.id ? revoked : k));
          this.notificationService.success('API key revoked successfully');
        }),
      ),
      errorMessage: error => apiErrorMessage(error, 'An error occurred while revoking the key. Please try again.'),
    }).subscribe();
  }

  delete(key: ApiKey): void {
    this.modalService.confirm({
      title: 'Delete API key?',
      message: `This will permanently delete the '${key.name || 'Untitled key'}' key and its record. This cannot be undone.`,
      confirmLabel: 'Delete key',
      pendingLabel: 'Deleting…',
      danger: true,
      onConfirm: () => this.apiKeysService.deleteApiKey(key.id).pipe(
        takeUntil(this._destroy$),
        tap(() => {
          this.keys = this.keys.filter(k => k.id !== key.id);
          this.revealedValues.delete(key.id);
          this.notificationService.success('API key deleted');
        }),
      ),
      errorMessage: error => apiErrorMessage(error, 'An error occurred while deleting the key. Please try again.'),
    }).subscribe();
  }

  isRevealed(key: ApiKey): boolean {
    return this.revealedValues.has(key.id);
  }

  isRevealing(key: ApiKey): boolean {
    return this.revealingIds.has(key.id);
  }

  displayValue(key: ApiKey): string {
    return this.revealedValues.get(key.id) ?? '';
  }

  toggleReveal(key: ApiKey): void {
    if (this.revealedValues.has(key.id)) {
      // Hiding is purely local no request, and the fetched value is dropped, not cached.
      this.revealedValues.delete(key.id);
      return;
    }
    if (this.revealingIds.has(key.id)) return;

    this.revealingIds.add(key.id);
    this.apiKeysService.revealApiKey(key.id)
      .pipe(
        takeUntil(this._destroy$),
        catchError(error => {
          this.notificationService.error('Could not reveal this key. Please try again.');
          return throwError(() => error);
        }),
        tap(plaintext => { this.revealedValues.set(key.id, plaintext); }),
        finalize(() => { this.revealingIds.delete(key.id); }),
      ).subscribe();
  }

  isCopied(key: ApiKey): boolean {
    return this.copiedIds.has(key.id);
  }

  copyValue(key: ApiKey): void {
    const value = this.revealedValues.get(key.id);
    if (!value) return;

    const showCopied = () => {
      this.copiedIds.add(key.id);
      setTimeout(() => this.copiedIds.delete(key.id), 2000);
    };

    if (navigator.clipboard) {
      navigator.clipboard.writeText(value).then(showCopied).catch(() => {
        this.fallbackCopy(value);
        showCopied();
      });
    } else {
      this.fallbackCopy(value);
      showCopied();
    }
  }

  private fallbackCopy(value: string): void {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
